import React from 'react';
import { track } from '@/lib/analytics/track';

interface UseSkeletonDelayOptions {
  /** Delay before showing skeleton (default: 150ms) */
  delay?: number;
  /** Minimum time to show skeleton once visible (default: 300ms) */
  minShow?: number;
  /** Context for analytics tracking */
  trackingContext?: string;
}

export function useSkeletonDelay(
  isLoading: boolean, 
  { 
    delay = 150,
    minShow = 300,
    trackingContext = 'unknown'
  }: UseSkeletonDelayOptions = {}
) {
  const [showSkeleton, setShowSkeleton] = React.useState(false);
  const [hideTimer, setHideTimer] = React.useState<number | null>(null);
  const [startTime, setStartTime] = React.useState<number | null>(null);

  React.useEffect(() => {
    if (isLoading) {
      // Start loading - set delay timer to show skeleton
      const showTimer = setTimeout(() => {
        setShowSkeleton(true);
        setStartTime(performance.now());
        track('skeleton_shown', {
          component: trackingContext,
          delayMs: delay,
          minShowMs: minShow,
          timestamp: Date.now()
        });
      }, delay);

      return () => {
        clearTimeout(showTimer);
        // If loading finished before skeleton was shown - track anti-flicker success
        if (!showSkeleton) {
          track('skeleton_flicker_prevented', {
            component: trackingContext,
            fastLoadingMs: performance.now() - (startTime || performance.now()),
            delayMs: delay
          });
        }
      };
    } else {
      // Loading finished
      if (showSkeleton) {
        // If skeleton is visible, ensure minimum show time
        const elapsed = startTime ? performance.now() - startTime : 0;
        const remainingTime = Math.max(0, minShow - elapsed);
        
        const timer = setTimeout(() => {
          setShowSkeleton(false);
          setStartTime(null);
          track('skeleton_hidden', {
            component: trackingContext,
            totalShowMs: elapsed + remainingTime,
            actualShowMs: elapsed,
            minShowEnforced: remainingTime > 0,
            timestamp: Date.now()
          });
        }, remainingTime) as unknown as number;
        
        setHideTimer(timer);
        return () => clearTimeout(timer);
      }
    }
  }, [isLoading, delay, minShow, trackingContext, showSkeleton, startTime]);

  // Cleanup hide timer on unmount
  React.useEffect(() => {
    return () => {
      if (hideTimer) clearTimeout(hideTimer);
    };
  }, [hideTimer]);

  return showSkeleton;
}

interface UseLoadingLatencyOptions {
  /** Context for analytics tracking */
  trackingContext?: string;
  /** Whether to track this loading cycle */
  enabled?: boolean;
}

export function useLoadingLatency(
  isLoading: boolean,
  { trackingContext = 'unknown', enabled = true }: UseLoadingLatencyOptions = {}
) {
  const startTime = React.useRef<number | null>(null);

  React.useEffect(() => {
    if (!enabled) return;

    if (isLoading) {
      // Loading started
      startTime.current = performance.now();
    } else if (startTime.current !== null) {
      // Loading finished
      const latency = performance.now() - startTime.current;
      track('loading_latency', {
        context: trackingContext,
        latencyMs: Math.round(latency)
      });
      startTime.current = null;
    }
  }, [isLoading, trackingContext, enabled]);

  React.useEffect(() => {
    // Cleanup on unmount
    return () => {
      startTime.current = null;
    };
  }, []);
}

/**
 * Combined hook for skeleton delay + latency tracking
 */
export function useSkeletonState(
  isLoading: boolean,
  options: UseSkeletonDelayOptions & UseLoadingLatencyOptions = {}
) {
  const showSkeleton = useSkeletonDelay(isLoading, options);
  useLoadingLatency(isLoading, options);
  
  return showSkeleton;
}